import type { LocaleCode, LinkedPage, Deal } from '@repo/types'

export interface SEOData {
  property?: {
    deals?: Deal[];
  };
  map?: {
    place?: LinkedPage;
    dealsCount?: number;
  }
  page?: LinkedPage;
}


// --- Helper Functions ---

export function capitalize(s: string): string {
  if (typeof s !== 'string' || s.length === 0) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function cleanAddressPart(s: string): string {
  return s
    .toLowerCase()
    .replace(/ (ken|to|do|fu|machi|ku|shi|mura)$/, '')
    .replace(/ (ken|to|do|fu|machi|ku|shi|mura) (.)/, ' $2')
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

export function getAddressPart(page: { slug: string; level: string } | undefined): string {
  if (!page?.slug) return '';
  return cleanAddressPart(page.slug.replace(/-/g, ' '))
}

export function getAddressFromDeal(deal: Deal, locale: LocaleCode): string | null {
  const localizedDeal = deal.i18n?.[locale];
  if (localizedDeal?.building?.addressComponents) {
    const { ken, shi, choaza } = localizedDeal.building.addressComponents;
    return [choaza, shi, ken].filter(Boolean).map(cleanAddressPart).join(', ');
  }
  return null
}

// Function to format layout (extracted from useSmartTitle)
function formatLayout(floorPlan: Deal['floorPlan'], layout: Deal['layout']): string {
    if (floorPlan === '1R' || floorPlan === '1K') return 'Studio';

    let rooms;
    // let type;
    if (layout) {
      // type = layout.type
      rooms = layout.roomCount
    } else if (floorPlan) {
      const match = floorPlan?.match(/^(\d+)(.*)$/);
      if (match?.[1]) {
        rooms = parseInt(match[1], 10);
        // type = match[2]
      }
    }

    if (rooms) {
      if (rooms === 1) return '1-Bedroom';
      return `${rooms}-Bedroom`;
    }

    return floorPlan ?? ''; // Fallback to original type if no match
}

const OLD_ADJECTIVES = ['Classic', 'Charming', 'Vintage', 'Well-Kept'];

// Core smart title generation logic (extracted from useSmartTitle)
export function generateSmartTitleString(
  deal: Deal,
  locale: LocaleCode,
  includeAddress: boolean = true
): string {
    if (locale === 'ja') {
        return deal?.name || deal?.building?.name || '';
    }

    const localizedDeal = deal.i18n?.[locale];
    if (localizedDeal?.building?.name) {
        return localizedDeal.building.name;
    }
    if (localizedDeal?.name) { // Fallback for localized deal name itself
      return localizedDeal.name;
    }


    const titleParts: string[] = [];

    // 1. Adjective Selection
    const adjectives: string[] = [];
    if (deal.building.builtAt) {
        const builtDate = new Date(deal.building.builtAt);
        const age = new Date().getFullYear() - builtDate.getFullYear();
        if (age <= 2) adjectives.push('Brand New');
        else if (age <= 10) adjectives.push('Modern');
        else if (age > 30) {
            const index = (deal.id?.charCodeAt(deal.id.length - 1) || 0) % OLD_ADJECTIVES.length;
            adjectives.push(OLD_ADJECTIVES[index]);
        }
    }
    if (deal.labels?.includes('リノベ済み')) adjectives.push('Renovated');
    if (deal.building.type !== 'land' && deal.price && deal.price > 150000000) adjectives.push('High-End');
    if (deal.sqm && deal.sqm > 100) adjectives.push('Spacious');

    const priorityAdjectives = ['Brand New', 'High-End', 'Renovated', 'Spacious', 'Modern'];
    let chosenAdjective = '';
    for (const adj of priorityAdjectives) {
        if (adjectives.includes(adj)) {
            chosenAdjective = adj;
            break;
        }
    }
    if (!chosenAdjective && adjectives.length > 0) {
        chosenAdjective = adjectives[0];
    }

    if (chosenAdjective) {
        titleParts.push(chosenAdjective);
    }

    // 2. Layout
    const formattedLayout = formatLayout(deal.floorPlan, deal.layout);
    if (formattedLayout) {
        titleParts.push(formattedLayout);
    }

    // 3. Property Type
    if (deal.building?.type) {
        const type = deal.building.type.toLowerCase();
        const typeMap: { [key: string]: string } = {
            'condo': 'Condo',
            'apartment': 'Apartment',
            'house': 'House',
            'land': 'Land',
            'other': 'Property'
        };
        titleParts.push(typeMap[type] || deal.building.type);
    }

    // 4. Location/Feature
    if (typeof deal.building?.nearestStations?.[0]?.minutes === 'number' && deal.building.nearestStations[0].minutes <= 7) {
        titleParts.push('Near Station');
    } else if (deal.labels?.includes('庭')) {
        titleParts.push('with a Garden');
    }
    if (includeAddress) {
      const localizedAddress = deal.i18n?.[locale]?.building?.addressComponents;
      if (localizedAddress?.choaza) {
        titleParts.push(`in ${localizedAddress.choaza}`);
      } else if (localizedAddress?.shi) {
        titleParts.push(`in ${localizedAddress.shi}`);
      }
    }

    if (titleParts.length > 0) { // Check if any parts were added
      return [...new Set(titleParts)].join(' ');
    }

    return deal.name || deal.building.name || 'Property';
}

// Removed getDisplayName and getDisplayAddress as generateSmartTitleString now encapsulates
// the core logic for the title. getDisplayAddress is also in apps/en/composables/address.ts and apps/en/pages/property/+title.ts
// which need to be updated.
